'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { MegaMenuWrapper } from './MegaMenuWrapper';
import { useReviewerAuth } from '../contexts/ReviewerAuthContext';
import ReviewerLoginPrompt from './ReviewerLoginPrompt';

const Header: React.FC = () => {
  const router = useRouter();
  const { reviewer, isLoggedIn, logout } = useReviewerAuth();
  const [cartCount, setCartCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement>(null);

  // Deployment version - UPDATE THIS before each deployment
  const DEPLOYMENT_VERSION = 'v1.0.0';
  const DEPLOYMENT_DATE = new Date().toISOString().split('T')[0]; // YYYY-MM-DD

  useEffect(() => {
    // Log deployment info to console
    console.log(`Camping Australia App Deployed`);
    console.log(`Version: ${DEPLOYMENT_VERSION}`);
    console.log(`Date: ${DEPLOYMENT_DATE}`);
    console.log(`Build: ${process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA?.substring(0, 7) || 'local'}`);

    // Fetch cart count from Shopify
    const fetchCartCount = async () => {
      try {
        const response = await fetch('https://campingaustralia.com.au/cart.js');
        const cartData = await response.json();
        setCartCount(cartData.item_count || 0);
      } catch (error) {
        console.error('Error fetching cart count:', error);
        setCartCount(0);
      }
    };

    fetchCartCount();

    // Listen for cart updates
    const handleCartUpdate = (event: any) => {
      if (event.detail && typeof event.detail.cartCount === 'number') {
        setCartCount(event.detail.cartCount);
      }
    };

    window.addEventListener('cartUpdated', handleCartUpdate);

    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
    };
  }, []);

  // Close account dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(e.target as Node)) {
        setShowAccountMenu(false);
      }
    };
    if (showAccountMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAccountMenu]);

  // ── Search handler ─────────────────────────────────────────
  const handleSearchSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const query = searchQuery.trim();
      if (!query) return;

      // Navigate to homepage with location param — the homepage
      // useEffect already handles ?location= to trigger a search.
      router.push(`/?location=${encodeURIComponent(query)}`);
    },
    [searchQuery, router],
  );

  // ── Account click handler ──────────────────────────────────
  const handleAccountClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (isLoggedIn) {
        setShowAccountMenu((prev) => !prev);
      } else {
        setShowLoginModal(true);
      }
    },
    [isLoggedIn],
  );

  // Reviewer display name
  const displayName = reviewer?.display_name
    ? reviewer.display_name.split(' ')[0]
    : reviewer?.email?.split('@')[0] || 'Account';

  return (
    <>
      {/* Deployment Version Badge - Only visible in development or with ?debug=true */}
      {(process.env.NODE_ENV === 'development' ||
        (typeof window !== 'undefined' && window.location.search.includes('debug=true'))) && (
        <div
          style={{
            position: 'fixed',
            top: '0',
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#ff6b6b',
            color: 'white',
            padding: '4px 12px',
            fontSize: '11px',
            fontWeight: 'bold',
            zIndex: 99999,
            borderRadius: '0 0 8px 8px',
            fontFamily: 'monospace',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}
        >
          APP v1.0.1 | Deploy: {new Date().toISOString().split('T')[0]}
        </div>
      )}

      {/* Desktop header */}
      <div className="header desktop-header" style={{background: '#ffffff', boxShadow: '0 2px 10px rgba(0,0,0,0.1)'}}>
        <div className="bar">
          <div className="logo-container">
            <Link href="/" className="logo">
              <img
                src="https://campingaustralia.com.au/cdn/shop/files/ca_website_logo_200x.png?v=1697249902"
                alt="Camping Australia"
                width="180"
                loading="eager"
              />
            </Link>
          </div>

          <div className="search-container">
            <form className="search-form" onSubmit={handleSearchSubmit}>
              <div className="search-input-wrapper">
                <input
                  type="search"
                  name="q"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search campsites, locations..."
                  aria-label="Search campsites"
                />
                <button type="submit" aria-label="Search campsites">
                  <i className="fc fc-search"></i>
                </button>
              </div>
            </form>
          </div>

          <div className="icons-container">
            <div ref={accountMenuRef} style={{ position: 'relative' }}>
              <a
                className="icon-user"
                href="#"
                onClick={handleAccountClick}
              >
                <i className="fc fc-user"></i>
                <span>{isLoggedIn ? displayName : 'Account'}</span>
              </a>

              {/* Logged-in account dropdown */}
              {showAccountMenu && isLoggedIn && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    minWidth: '200px',
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    zIndex: 1000,
                    padding: '8px 0',
                    marginTop: '4px',
                  }}
                >
                  <div style={{ padding: '8px 16px', borderBottom: '1px solid #f3f4f6' }}>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>
                      {reviewer?.display_name || 'User'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      {reviewer?.email || ''}
                    </div>
                  </div>
                  <Link
                    href="/apps/campsite-explorer/saved"
                    style={{
                      display: 'block',
                      padding: '8px 16px',
                      fontSize: '14px',
                      color: '#374151',
                      textDecoration: 'none',
                    }}
                    onClick={() => setShowAccountMenu(false)}
                  >
                    Saved Trips
                  </Link>
                  <Link
                    href="/apps/campsite-explorer/recommend-campsite"
                    style={{
                      display: 'block',
                      padding: '8px 16px',
                      fontSize: '14px',
                      color: '#374151',
                      textDecoration: 'none',
                    }}
                    onClick={() => setShowAccountMenu(false)}
                  >
                    Suggest a Campsite
                  </Link>
                  <a
                    href="https://campingaustralia.com.au/account"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'block',
                      padding: '8px 16px',
                      fontSize: '14px',
                      color: '#374151',
                      textDecoration: 'none',
                    }}
                    onClick={() => setShowAccountMenu(false)}
                  >
                    Shop Account
                  </a>
                  <button
                    onClick={() => {
                      logout();
                      setShowAccountMenu(false);
                    }}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '8px 16px',
                      fontSize: '14px',
                      color: '#dc2626',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      borderTop: '1px solid #f3f4f6',
                    }}
                  >
                    Sign Out
                  </button>
                </div>
              )}
            </div>
            <a className="icon-cart" href="https://campingaustralia.com.au/cart" target="_blank">
              <i className="fc fc-cart"></i>
              <span>Cart</span>
              <span className={`cart-count ${cartCount === 0 ? 'hidden-count' : ''}`}>
                {cartCount}
              </span>
            </a>
          </div>
        </div>
      </div>

      {/* Mobile header */}
      <div className="mobile-header">
        <button className="toggle-menu" aria-label="Menu">
          <i className="fc fc-hamburguer"></i>
        </button>

        <div className="logo-container">
          <Link href="/" className="logo">
            <img
              src="https://campingaustralia.com.au/cdn/shop/files/ca_website_logo_200x.png?v=1697249902"
              alt="Camping Australia"
              width="120"
            />
          </Link>
        </div>

        <div className="icons-container">
          <a className="icon-user" href="#" onClick={handleAccountClick}>
            <i className="fc fc-user"></i>
          </a>
          <a className="icon-cart" href="https://campingaustralia.com.au/cart" target="_blank">
            <i className="fc fc-cart"></i>
            <span className={`cart-count ${cartCount === 0 ? 'hidden-count' : ''}`}>
              {cartCount}
            </span>
          </a>
        </div>
      </div>

      {/* Mobile search */}
      <div className="mobile-search">
        <form className="search-form" onSubmit={handleSearchSubmit}>
          <div className="search-input-wrapper">
            <input
              type="search"
              name="q"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search campsites, locations..."
              aria-label="Search campsites"
            />
            <button type="submit" aria-label="Search campsites">
              <i className="fc fc-search"></i>
            </button>
          </div>
        </form>
      </div>

      {/* USP Trust Bar */}
      <div className="usp-bar" aria-label="Key benefits">
        <div className="usp-bar__inner">
          <div className="usp-bar__item">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="1 4 1 11 8 11"/><path d="M3.51 15a9 9 0 1 0 .49-4.5"/></svg>
            <span>30-day returns</span>
          </div>
          <div className="usp-bar__divider" aria-hidden="true"></div>
          <div className="usp-bar__item">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            <span>Secure checkout</span>
          </div>
          <div className="usp-bar__divider" aria-hidden="true"></div>
          <div className="usp-bar__item">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            <span>Australian owned &amp; operated</span>
          </div>
          <div className="usp-bar__divider usp-bar__divider--hide-mobile" aria-hidden="true"></div>
          <div className="usp-bar__item usp-bar__item--hide-mobile">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.4 2 2 0 0 1 3.6 1.22h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.5a16 16 0 0 0 6 6l.94-.94a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.73 16z"/></svg>
            <span>Expert advice: 03 9088 0386</span>
          </div>
        </div>
      </div>

      {/* THIS IS WHERE MEGAMENUWRAPPER IS USED - AT THE VERY END */}
      <MegaMenuWrapper />

      {/* Login modal — shown when unauthenticated user clicks Account */}
      <ReviewerLoginPrompt
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        title="Sign in to your account"
        subtitle="Sign in with Google, Facebook, or Shopify to leave reviews, upload photos, and manage your profile."
      />
    </>
  );
};

export default Header;
